import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface Account {
  id: number;
  email: string;
  name: string;
  role: string;
  plan: string | null;
  associatedUserId: number | null;
  createdAt: string;
  isMain: boolean;
}

interface PlanSelectionScreenProps {
  accounts: Account[];
  onPlanSelect: (accountId: number) => void;
}

const PlanSelectionScreen: React.FC<PlanSelectionScreenProps> = ({ accounts, onPlanSelect }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('userAccounts');
    localStorage.removeItem('currentUser');
    navigate('/');
  };

  useEffect(() => {
    setTimeout(() => setIsLoaded(true), 100);
    return () => setIsLoaded(false);
  }, []);

  // Redirigir automáticamente si el usuario es ADMIN
  useEffect(() => {
    const adminAccount = accounts.find(account => account.role === 'ADMIN');
    if (adminAccount) {
      navigate('/admin-users');
    }
  }, [accounts, navigate]);

  const handlePlanSelect = async (account: Account) => {
    if (isProcessing) return;
    setSelectedAccount(account);
    setIsProcessing(true);
    try {
      await onPlanSelect(account.id);
    } catch (error) {
      console.error('Error al seleccionar plan:', error);
      setIsProcessing(false);
      setSelectedAccount(null);
    }
  };

  const getPlanDisplayName = (plan: string | null) => {
    if (!plan) return 'Sin plan';
    if (plan.startsWith('SEO')) {
      const count = plan.includes('_2M') ? '2' : '4';
      return `SEO - ${count} publicaciones/mes`;
    } else if (plan.startsWith('RRSS')) {
      const count = plan.includes('_4M') ? '4' : plan.includes('_6M') ? '6' : '12';
      return `Redes Sociales - ${count} publicaciones/mes`;
    }
    return plan;
  };

  const getPlanIcon = (plan: string | null) => {
    if (!plan) return null;
    if (plan.startsWith('SEO')) {
      return (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      );
    } else if (plan.startsWith('RRSS')) {
      return (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
        </svg>
      );
    }
    return null;
  };

  const getPlanColor = (plan: string | null) => {
    if (!plan) return 'bg-gray-500';
    if (plan.startsWith('SEO')) {
      return 'bg-green-500';
    } else if (plan.startsWith('RRSS')) {
      return 'bg-blue-500';
    }
    return 'bg-gray-500';
  };

  return (
    <div className="relative min-h-screen bg-[#F8F9FA]">
      {/* VERSIÓN MÓVIL */}
      <div
        className={`block sm:hidden bg-[#F8F9FA] min-h-screen w-full font-futura px-0 pb-8 relative transition-all duration-700 ease-in-out ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
        style={{ pointerEvents: isLoaded ? 'auto' : 'none' }}
      >
        {/* Spinner de carga */}
        {!isLoaded && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#F8F9FA] bg-opacity-80">
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full border-4 border-blue-200 border-t-[#44aaf6] w-16 h-16 mb-4" style={{ borderTopColor: '#44aaf6', borderRightColor: '#e0e7ef', borderBottomColor: '#e0e7ef', borderLeftColor: '#e0e7ef' }}></div>
              <span className="text-[#44aaf6] font-bold text-lg tracking-wide">Cargando planes…</span>
            </div>
          </div>
        )}
        
        {/* Botón Cerrar sesión */}
        <div className="flex justify-end px-4 pt-6">
          <button
            onClick={handleLogout}
            className="px-4 py-1.5 rounded-full bg-white shadow text-[#44aaf6] font-bold text-sm border border-white hover:bg-blue-50 transition-all"
            style={{ letterSpacing: '0.08em' }}
          >
            Cerrar sesión
          </button>
        </div>

        {/* Header */}
        <div className="px-6 pt-8 pb-6">
          <h1 className="text-3xl font-bold text-[#5AB2F7] mb-3 text-center leading-tight" style={{ letterSpacing: '0.06em' }}>
            Selecciona tu plan
          </h1>
          <p className="text-gray-600 text-base text-center leading-relaxed">
            Tienes múltiples planes activos. Elige con cuál quieres acceder al calendario.
          </p>
        </div>

        {/* Planes */}
        <div className="px-6 space-y-4">
          {accounts.map((account) => (
            <div
              key={account.id}
              className={`relative cursor-pointer rounded-2xl border-2 flex items-center gap-4 p-4 transition-all duration-300 ${
                selectedAccount?.id === account.id
                  ? 'border-[#44aaf6] bg-blue-50 shadow-lg scale-[1.02]'
                  : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'
              }`}
              onClick={() => handlePlanSelect(account)}
              style={{ minHeight: '80px' }}
            >
              {/* Icono del plan */}
              <div className={`w-14 h-14 rounded-full flex items-center justify-center text-white text-xl ${getPlanColor(account.plan)} shadow-md`}>
                {getPlanIcon(account.plan)}
              </div>
              {/* Información del plan */}
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-lg text-gray-800 mb-1 truncate">
                  {getPlanDisplayName(account.plan)}
                </h3>
                <p className="text-gray-500 text-sm truncate">{account.name}</p>
              </div>
              {/* Indicador de selección */}
              {selectedAccount?.id === account.id && (
                <div className="w-7 h-7 bg-[#44aaf6] rounded-full flex items-center justify-center shadow-md">
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Indicador de carga si está procesando */}
        {isProcessing && (
          <div className="mt-6 px-6 flex items-center justify-center gap-3 text-[#44aaf6]">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#44aaf6]"></div>
            <span className="text-sm font-semibold">Cambiando a tu plan seleccionado...</span>
          </div>
        )}
      </div>

      {/* VERSIÓN ESCRITORIO */}
      <div className="hidden sm:block">
        <div
          className="bg-[#F8F9FA] min-h-screen w-full font-futura px-2 sm:px-8 pb-12 relative transition-all duration-350 ease-in-out"
          style={{
            opacity: isLoaded ? 1 : 0
          }}
        >
          {/* Botón Cerrar sesión */}
          <div className="absolute top-6 right-10 z-30">
            <button
              onClick={handleLogout}
              className="px-8 py-3 rounded-full bg-white shadow text-[#44aaf6] font-bold tracking-wide text-lg border border-white hover:bg-blue-50 transition-all"
              style={{ letterSpacing: '0.08em' }}
            >
              Cerrar sesión
            </button>
          </div>

          {/* Header */}
          <div className="flex flex-col items-center pt-16 pb-12 max-w-4xl mx-auto">
            <h1 className="text-5xl sm:text-6xl font-bold text-[#5AB2F7] mb-6 text-center leading-tight tracking-wide" style={{ letterSpacing: '0.06em' }}>
              Selecciona tu plan
            </h1>
            <p className="text-xl text-gray-600 text-center max-w-2xl leading-relaxed">
              Tienes múltiples planes activos. Elige con cuál quieres acceder al calendario.
            </p>
          </div>

          {/* Planes */}
          <div className="max-w-4xl mx-auto px-4">
            <div className="grid gap-6">
              {accounts.map((account) => (
                <div
                  key={account.id}
                  className={`relative cursor-pointer rounded-3xl border-2 flex items-center gap-8 p-8 transition-all duration-300 ${
                    selectedAccount?.id === account.id
                      ? 'border-[#44aaf6] bg-blue-50 shadow-xl scale-[1.02]'
                      : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-lg hover:scale-[1.01]'
                  }`}
                  onClick={() => handlePlanSelect(account)}
                  style={{ minHeight: '120px' }}
                >
                  {/* Icono del plan */}
                  <div className={`w-20 h-20 rounded-full flex items-center justify-center text-white text-3xl ${getPlanColor(account.plan)} shadow-lg`}>
                    {getPlanIcon(account.plan)}
                  </div>
                  {/* Información del plan */}
                  <div className="flex-1">
                    <h3 className="font-semibold text-2xl text-gray-800 mb-2">
                      {getPlanDisplayName(account.plan)}
                    </h3>
                    <p className="text-gray-500 text-lg">{account.name}</p>
                  </div>
                  {/* Indicador de selección */}
                  {selectedAccount?.id === account.id && (
                    <div className="w-10 h-10 bg-[#44aaf6] rounded-full flex items-center justify-center shadow-lg">
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Indicador de carga si está procesando */}
          {isProcessing && (
            <div className="mt-8 flex items-center justify-center gap-3 text-[#44aaf6]">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#44aaf6]"></div>
              <span className="text-lg font-semibold">Cambiando a tu plan seleccionado...</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlanSelectionScreen; 